import React from "react";
import Layout from "../../components/Layout/Layout";
import {
    NOT_REPORTED,
    consensusMechanisms,
    cryptoAssetsCovered,
    dataLimitations,
    dataProviders,
    energyConsumption,
    generalInformation,
    ghgEmissions,
    meta,
    methodologicalAssumptions,
    methodologySources,
    renewableEnergy,
    reviewTriggers,
} from "./pai-data";

/**
 * Path to the machine-readable annex in the template prescribed by the Annex to
 * Commission Delegated Regulation (EU) 2025/422.
 *
 * TODO: set this to the published file (e.g. "/legal/everest-pai-annex-2026.csv") once the
 * annex file has been supplied and signed off. The Introduction below states that the file is
 * "available for download free of charge on this page" — that statement is not satisfied while
 * this is null, so the file must be in place before this page is published.
 */
const ANNEX_FILE = null;

const cardStyle = { marginBottom: 18 };
const headingStyle = { marginTop: 0, marginBottom: 12, fontSize: "1.25rem" };
const subHeadingStyle = { fontSize: "1.05rem", marginTop: 20, marginBottom: 8 };

// Indicator not reported by the underlying data provider; explained under Data Limitations.
const NotReported = () => (
    <span className="not-reported" title="Not reported by the data provider" aria-label="Not reported by the data provider">
        {NOT_REPORTED}
    </span>
);

const Cell = ({ value, className }) =>
    value === NOT_REPORTED ? (
        <td className={className}>
            <NotReported />
        </td>
    ) : (
        <td className={className}>{value}</td>
    );

const DataTable = ({ label, headers, children, prose }) => (
    <div className="data-table-wrap" role="region" aria-label={label} tabIndex={0}>
        <table className={`data-table${prose ? " data-table--prose" : ""}`}>
            <thead>
                <tr>
                    {headers.map((header) => (
                        <th key={header} scope="col">
                            {header}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>{children}</tbody>
        </table>
    </div>
);

const PrincipalAdverseImpacts = () => {
    return (
        <Layout>
            <section className="page-hero">
                <div className="container">
                    <div className="eyebrow">
                        <span className="dot"></span> Principal Adverse Impacts
                    </div>
                    <h1>Principal Adverse Impacts on Climate and Other Environment-Related Adverse Impacts</h1>
                    <p>Everest Network Ltd. — last updated: {meta.lastUpdated}</p>
                </div>
            </section>

            <section className="section section-tight">
                <div className="container" style={{ maxWidth: 980 }}>
                    <article className="card" style={cardStyle}>
                        <h2 style={headingStyle}>Introduction</h2>
                        <p>
                            Everest Network Ltd. (“Everest”, “we”, “our” or “us”) publishes this disclosure in accordance
                            with Article 66(5) of Regulation (EU) 2023/1114 on Markets in Crypto-Assets (“MiCA”) and
                            Commission Delegated Regulation (EU) 2025/422.
                        </p>
                        <p style={{ marginTop: 12 }}>
                            This disclosure provides information relating to the principal adverse impacts on the climate
                            and other environment-related adverse impacts associated with the consensus mechanisms used
                            by the crypto-assets in relation to which Everest provides MiCA-regulated crypto-asset
                            services.
                        </p>
                        <p style={{ marginTop: 12 }}>
                            The information contained in this disclosure has been prepared using information obtained
                            from crypto-asset issuers, publicly available information, independent third-party data
                            providers and, where permitted under applicable legislation, reasonable estimates where
                            complete information is not available. Material assumptions and estimates are identified in
                            the “Methodological Assumptions” and “Data Limitations” sections below.
                        </p>
                        <p style={{ marginTop: 12 }}>
                            A machine-readable file containing the full set of sustainability indicators for each
                            crypto-asset, in the template prescribed by the Annex to Commission Delegated Regulation (EU)
                            2025/422, is{" "}
                            <a
                                href="/Everest_PAI_Disclosure_July_2026.pdf"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: "var(--brand)", textDecoration: "underline" }}
                            >
                                available for download
                            </a>{" "}
                            free of charge on this page.
                        </p>
                        {ANNEX_FILE && (
                            <p style={{ marginTop: 14 }}>
                                <a className="btn btn-secondary" href={ANNEX_FILE} download>
                                    Download the machine-readable annex
                                </a>
                            </p>
                        )}
                        <p style={{ marginTop: 12 }}>
                            This page is reviewed at least annually and updated without undue delay where material
                            changes occur, with a clear indication of the changes made.
                        </p>
                    </article>

                    <article className="card" style={cardStyle}>
                        <h2 style={headingStyle}>General Information</h2>
                        <DataTable label="General information" headers={["Item", "Information"]}>
                            {generalInformation.map(([item, information]) => (
                                <tr key={item}>
                                    <th scope="row">{item}</th>
                                    <td>{information}</td>
                                </tr>
                            ))}
                        </DataTable>
                    </article>

                    <article className="card" style={cardStyle}>
                        <h2 style={headingStyle}>Scope of this Disclosure</h2>
                        <p>
                            This disclosure applies to the crypto-assets in relation to which Everest provides
                            MiCA-regulated crypto-asset services (custody and administration, transfer services, placing,
                            execution of orders on behalf of clients, and reception and transmission of orders), as
                            listed in Annex A of Everest’s Programme of Operations.
                        </p>
                        <p style={{ marginTop: 12 }}>
                            The information on this page relates solely to the sustainability indicators associated with
                            the consensus mechanisms of the distributed ledger networks on which those crypto-assets are
                            transacted. In accordance with Commission Delegated Regulation (EU) 2025/422, the indicators
                            relate to each network as a whole — covering all nodes involved in validating transactions
                            and maintaining the integrity of the distributed ledger — and are not apportioned to
                            Everest’s or its clients’ share of network activity.
                        </p>
                        <p style={{ marginTop: 12 }}>
                            Everest does not operate or control any public blockchain protocol, consensus mechanism,
                            validator infrastructure or mining operations, with the exception of EverChain, a
                            permissioned EVM-compatible network operated by Everest for the issuance of wrapped tokens.
                            This disclosure should not be interpreted as an assessment of the sustainability performance
                            of Everest itself or of any crypto-asset issuer.
                        </p>
                    </article>

                    <article className="card" style={cardStyle}>
                        <h2 style={headingStyle}>Methodology</h2>
                        <p>
                            Everest prepares this disclosure in accordance with the methodologies prescribed by
                            Commission Delegated Regulation (EU) 2025/422 (which refer to the European Sustainability
                            Reporting Standards, ESRS E1). Information is obtained from one or more of the following
                            sources:
                        </p>
                        <ul className="check-list micro-list">
                            {methodologySources.map((source) => (
                                <li key={source}>{source}</li>
                            ))}
                        </ul>
                        <p style={{ marginTop: 14 }}>
                            All quantitative indicators disclosed on this page are estimates prepared by independent
                            third parties, since exact node counts, hardware configurations and locations cannot be
                            directly observed on public networks. All figures are gross: no carbon offsetting or other
                            market-based mechanism is netted against any indicator (Article 6(6)).
                        </p>
                    </article>

                    <article className="card" style={cardStyle}>
                        <h2 style={headingStyle}>Crypto-Assets Covered</h2>
                        <DataTable
                            label="Crypto-assets covered"
                            headers={["Symbol", "Crypto-asset", "Category", "Network", "Consensus mechanism"]}
                        >
                            {cryptoAssetsCovered.map(([symbol, asset, category, network, consensus]) => (
                                <tr key={`${symbol}-${asset}`}>
                                    <th scope="row">{symbol}</th>
                                    <td>{asset}</td>
                                    <td>{category}</td>
                                    <td>{network}</td>
                                    <td>{consensus}</td>
                                </tr>
                            ))}
                        </DataTable>
                        <p className="table-note">
                            Note: certain assets listed in Annex A (including LTC, BCH and DOT) are supported by Everest
                            as ERC-20 representations on Ethereum Mainnet. For LTC and BCH, the indicators of the native
                            Litecoin and Bitcoin Cash networks are disclosed on a prudent basis in addition to
                            Ethereum’s. Where a token also exists natively on another distributed ledger, this disclosure
                            covers the representation serviced by Everest.
                        </p>
                    </article>

                    <article className="card" style={cardStyle}>
                        <h2 style={headingStyle}>Consensus Mechanisms and Incentive Structures</h2>
                        <p>
                            In accordance with Article 6(1), point (b), of Commission Delegated Regulation (EU) 2025/422,
                            the table below describes, for each distributed ledger network, the features of the consensus
                            mechanism used for the validation of transactions and the maintenance of ledger integrity,
                            and the associated incentive structure — including block rewards, staking mechanics, slashing
                            or penalty arrangements, and applicable fees (Annex fields S.4 and S.5).
                        </p>
                        <DataTable
                            label="Consensus mechanisms and incentive structures"
                            headers={["Network", "Consensus mechanism (S.4)", "Incentive structure and applicable fees (S.5)"]}
                            prose
                        >
                            {consensusMechanisms.map(({ network, s4, s5 }) => (
                                <tr key={network}>
                                    <th scope="row">{network}</th>
                                    <td>{s4}</td>
                                    <td>{s5}</td>
                                </tr>
                            ))}
                        </DataTable>
                        <p className="table-note">
                            ERC-20 tokens supported on Ethereum Mainnet are validated by Ethereum’s consensus mechanism
                            and are subject to Ethereum’s incentive structure as described above.
                        </p>
                    </article>

                    <article className="card" style={cardStyle}>
                        <h2 style={headingStyle}>Principal Adverse Impact Indicators</h2>
                        <p>
                            The mandatory key indicator (S.8, energy consumption) and, on a voluntary basis, the
                            supplementary key indicators of Table 3 of the Annex to Commission Delegated Regulation (EU)
                            2025/422 are summarised per network below. The downloadable annex file sets out the
                            indicators per individual crypto-asset in the prescribed template.
                        </p>

                        <h3 style={subHeadingStyle}>Energy Consumption (S.8)</h3>
                        <DataTable
                            label="Energy consumption"
                            headers={["Network", "Asset(s)", "Reference period", "Energy (kWh/yr)", "Primary source"]}
                        >
                            {energyConsumption.map(([network, assets, period, energy, source]) => (
                                <tr key={network}>
                                    <th scope="row">{network}</th>
                                    <td>{assets}</td>
                                    <td className="num">{period}</td>
                                    <td className="num">{energy}</td>
                                    <td>{source}</td>
                                </tr>
                            ))}
                        </DataTable>
                        <p className="table-note">
                            Tokens supported as ERC-20 on Ethereum Mainnet (LINK, PENGU, HYPE, PEPE, AAVE, ONDO, SHIB,
                            PNUT, XPL, UNI, WIF, ARB, DOT, BONK, TRUMP, WLFI, CRV, SPARK) take the Ethereum network
                            figures above.
                        </p>

                        <h3 style={subHeadingStyle}>Greenhouse Gas Emissions (S.12 / S.13)</h3>
                        <DataTable
                            label="Greenhouse gas emissions"
                            headers={[
                                "Network",
                                "Scope 1 (tCO₂e/yr)",
                                "Scope 2 (tCO₂e/yr)",
                                "GHG intensity (kgCO₂e/tx)",
                            ]}
                        >
                            {ghgEmissions.map(([network, scope1, scope2, intensity]) => (
                                <tr key={network}>
                                    <th scope="row">{network}</th>
                                    <Cell value={scope1} className="num" />
                                    <Cell value={scope2} className="num" />
                                    <Cell value={intensity} className="num" />
                                </tr>
                            ))}
                        </DataTable>
                        <p className="table-note">
                            GHG figures are location-based Scope 2 estimates of the electricity used by network nodes and
                            miners; Scope 1 emissions are reported as zero across all networks by all sources. Figures
                            are gross of any offsetting.
                        </p>

                        <h3 style={subHeadingStyle}>Renewable Energy and Energy Intensity (S.10 / S.11)</h3>
                        <DataTable
                            label="Renewable energy and energy intensity"
                            headers={["Network", "Renewable share of energy", "Energy intensity (kWh/tx)", "Note"]}
                        >
                            {renewableEnergy.map(([network, renewable, intensity, note]) => (
                                <tr key={network}>
                                    <th scope="row">{network}</th>
                                    <Cell value={renewable} className="num" />
                                    <Cell value={intensity} className="num" />
                                    <td>{note}</td>
                                </tr>
                            ))}
                        </DataTable>
                        <p className="table-note">
                            The renewable share for Proof-of-Work networks (Bitcoin, Dogecoin, Litecoin, Bitcoin Cash) is
                            a modelled generic mining-mix estimate (33.61%) applied by the data provider, not a measured
                            value. The Cambridge Centre for Alternative Finance reports a higher sustainable-energy share
                            for Bitcoin mining (52.4%, including nuclear, April 2025 study).
                        </p>
                    </article>

                    <article className="card" style={cardStyle}>
                        <h2 style={headingStyle}>Third-Party Data Providers</h2>
                        <DataTable label="Third-party data providers" headers={["Provider", "Information provided"]} prose>
                            {dataProviders.map(([provider, information]) => (
                                <tr key={provider}>
                                    <th scope="row">{provider}</th>
                                    <td>{information}</td>
                                </tr>
                            ))}
                        </DataTable>
                    </article>

                    <article className="card" style={cardStyle}>
                        <h2 style={headingStyle}>Methodological Assumptions</h2>
                        <DataTable label="Methodological assumptions" headers={["Assumption", "Description"]} prose>
                            {methodologicalAssumptions.map(([assumption, description]) => (
                                <tr key={assumption}>
                                    <th scope="row">{assumption}</th>
                                    <td>{description}</td>
                                </tr>
                            ))}
                        </DataTable>
                    </article>

                    <article className="card" style={cardStyle}>
                        <h2 style={headingStyle}>Data Limitations</h2>
                        <p>
                            Sustainability information relating to crypto-assets and their consensus mechanisms continues
                            to evolve, and published figures differ materially between providers depending on
                            methodology, attribution assumptions and reference period. Where more than one source exists,
                            Everest discloses the figure it considers most reliable, generally preferring the more
                            conservative (higher-impact) estimate, and identifies material divergences:
                        </p>
                        <ul className="check-list micro-list">
                            {dataLimitations.map((limitation) => (
                                <li key={limitation}>{limitation}</li>
                            ))}
                        </ul>
                    </article>

                    <article className="card" style={cardStyle}>
                        <h2 style={headingStyle}>Review of this Disclosure</h2>
                        <p>
                            Everest reviews this disclosure at least annually and updates it without undue delay, with a
                            clear indication of the changes made, where:
                        </p>
                        <ul className="check-list micro-list">
                            {reviewTriggers.map((trigger) => (
                                <li key={trigger}>{trigger}</li>
                            ))}
                        </ul>
                    </article>

                    <article className="card">
                        <h2 style={headingStyle}>Regulatory Statement</h2>
                        <p>
                            This disclosure is published in accordance with Regulation (EU) 2023/1114 on Markets in
                            Crypto-Assets (“MiCA”), in particular Article 66(5), and Commission Delegated Regulation (EU)
                            2025/422 supplementing MiCA with regard to regulatory technical standards specifying the
                            content, methodologies and presentation of information in respect of sustainability
                            indicators in relation to adverse impacts on the climate and other environment-related
                            adverse impacts.
                        </p>
                        <p style={{ marginTop: 12 }}>
                            The information is made available free of charge, in a downloadable format, and is presented
                            so as to facilitate comparison between the crypto-assets in relation to which Everest
                            provides services. Everest maintains this disclosure in accordance with its ongoing
                            regulatory obligations.
                        </p>
                        <p style={{ marginTop: 12 }}>
                            This disclosure is published in English, a language customary in the sphere of international
                            finance, in accordance with Article 3(3) of Commission Delegated Regulation (EU) 2025/422. On
                            that basis the information is made available in English in respect of all Member States in
                            which Everest provides crypto-asset services.
                        </p>
                    </article>
                </div>
            </section>
        </Layout>
    );
};

export default PrincipalAdverseImpacts;
